import type { FirebaseMessaging, MessagePayload } from '@firebase/messaging-types';
import { webpushConfig } from 'reactApp/appHelpers/featuresHelpers/features/webPush';
import { sendXray } from 'reactApp/utils/ga';

import { api, FEATURE_NAME, log } from './shared';
import { getEmailsHash, getWebPushAccounts, isTokenSentToServer, setTokenSentToServer, trackSubscriptionSent } from './webPush.storage';

/** Отправить FCM-токен на сервер (дедуп по sha1(emails)). */
export async function sendFcmTokenToServer(token: string) {
    const accounts = await getWebPushAccounts();
    const emailsHash = getEmailsHash(accounts);
    const accountsEmails = accounts.map((account) => ({
        email: account.email,
    }));

    if (isTokenSentToServer(emailsHash)) {
        log("FCM token already sent to server so won't send it again unless it changes");
        return;
    }

    log('Sending FCM token to server...');

    try {
        const res = await api.makeRequest({
            settings: {},
            platform: 'webpush',
            push_token: token,
            push_tags: ['Web'],
            accounts: accountsEmails,
        });

        setTokenSentToServer(emailsHash);
        trackSubscriptionSent('fcm');
        log('FCM token sent, status:', res?.status);
    } catch (error) {
        log('Failed to send FCM token. ', error);
        setTokenSentToServer(false);
    }
}

/**
 * Инициализировать firebase messaging. SDK грузится ДИНАМИЧЕСКИ (import('firebase/*')) —
 * поэтому pushme, который этот модуль не вызывает, не тянет увесистый firebase-чанк
 * (в модуле нет статических runtime-импортов firebase, только `import type`).
 * Возвращает messaging или undefined (не поддержано / ошибка импорта).
 */
export async function initFcmMessaging(): Promise<FirebaseMessaging | undefined> {
    // eslint-disable-next-line init-declarations, @typescript-eslint/consistent-type-imports
    let fb: typeof import('firebase/app') | undefined;

    try {
        // https://jira.vk.team/browse/CLOUDWEB-16116
        // В режиме lockdown на маках\ios внутри firebase не обрабатывается ошибка отсутствия
        // апи IDBIndex, потому ловим сами.
        [, fb] = await Promise.all([import('firebase/messaging'), import('firebase/app')]);
    } catch (error) {
        log('Firebase import failed', error);
        return undefined;
    }

    const firebase = fb?.default;

    if (!firebase) {
        log('Firebase import returned empty');
        return undefined;
    }

    try {
        if (!firebase.messaging.isSupported()) {
            log('Firebase messaging not supported');
            sendXray([FEATURE_NAME, 'not-supported', 'fcm']);
            return undefined;
        }

        firebase.initializeApp(webpushConfig.firebaseConfig);

        const messaging = firebase.messaging();

        // Входящее сообщение при активной вкладке (foreground).
        messaging.onMessage((payload: MessagePayload) => {
            log('Message received.', payload);
        });

        return messaging;
    } catch (error) {
        sendXray([FEATURE_NAME, 'fcm', 'exception']);
        log(error);
        return undefined;
    }
}
